import React, { useEffect, useRef, useState } from 'react';
import { Box, Center, SimpleGrid, Text } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getFirstDayOfWeek } from 'clipsal-cortex-utils/src/calculations/date-utils';
import { formatDate } from 'clipsal-cortex-utils/src/formatting/formatting';

import { get } from '../../../common/api/api-helpers';
import { DataSummaryCard } from '../../../common/components/DataSummaryCard';
import { DateRangeType, DateRangeTypePicker } from '../../../common/components/DateRangeTypePicker';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { getDateRangeTypeSufix } from '../../../utils/common/common-utils';
import { ActivityDatePicker } from '../../activity/common/ActivityDatePicker';
import { useDateRangeOptions } from '../../activity/common/use-date-range-options';
import { getInitialSelectedDates } from '../../activity/utils';
import { selectCurrentDayForSite } from '../../site/siteSlice';
import { selectEvChargerData } from '../dashboard/evChargerSlice';
import { formatSecondsToHHMM } from '../ev-charger-helper';
import {
  AggregationType,
  ChargingActivityData,
  ChargingActivityRawData,
  ChargingActivityRecordsRawData,
  ChargingActivitySessionData,
} from '../types';
import { ChargingSessionList } from './ChargingSessionList';
import { ChargingTotalCostCard } from './ChargingTotalCostCard';
import { HistoryChart } from './HistoryChart';

export interface ChargingActivityState {
  selectedDateRangeType: DateRangeType;
  rangeTypeToSelectedPeriod: Record<DateRangeType, Date>;
  isCostViewSelected: boolean;
  isLoaded: boolean;
}

export function ChargingActivity() {
  const DATE_RANGE_OPTIONS = useDateRangeOptions();
  const { t } = useTranslation();
  const { siteDeviceId } = useSelector(selectEvChargerData);
  const currentDay = useSelector(selectCurrentDayForSite);
  const dataCache = useRef<Map<string, ChargingActivityData>>(new Map());
  const initialState: ChargingActivityData = {
    totalCost: '',
    totalChargeTime: '',
    totalEnergyConsumed: '',
    savings: 0,
    chargingSessions: [],
  };
  const [state, setState] = useState<ChargingActivityState>({
    selectedDateRangeType: DateRangeType.Day,
    rangeTypeToSelectedPeriod: getInitialSelectedDates(currentDay),
    isCostViewSelected: false,
    isLoaded: true,
  });

  const [data, setData] = useState<ChargingActivityData>(initialState);

  const { selectedDateRangeType, rangeTypeToSelectedPeriod, isCostViewSelected, isLoaded } = state;
  const dateRangeSufix = getDateRangeTypeSufix(selectedDateRangeType);

  const handleSelectChart = (isCostViewSelected: boolean) => {
    setState((prevState) => ({ ...prevState, isCostViewSelected }));
  };

  useEffect(() => {
    if (!siteDeviceId) return;

    const currentKey = `${siteDeviceId}-${selectedDateRangeType}-${rangeTypeToSelectedPeriod[
      selectedDateRangeType
    ]?.toISOString()}`;

    const cachedData = dataCache.current.get(currentKey);
    if (cachedData) {
      setData(cachedData);
      setState((prev) => ({ ...prev, isLoaded: true }));
      return;
    }

    const loadData = async () => {
      setState((prev) => ({ ...prev, isLoaded: false }));

      const fetchedData = await fetchChargingActivity(siteDeviceId, selectedDateRangeType, rangeTypeToSelectedPeriod);

      setData(fetchedData);
      setState((prev) => ({ ...prev, isLoaded: true }));

      dataCache.current.set(currentKey, fetchedData);
    };

    loadData();
  }, [selectedDateRangeType, rangeTypeToSelectedPeriod, siteDeviceId]);

  function handleDateRangeTypeChange(newDateRangeType: DateRangeType) {
    setState((prevState) => ({
      ...prevState,
      selectedDateRangeType: newDateRangeType,
    }));
  }

  function handleDateChange(newDate: Date | null) {
    setState((prevState) => ({
      ...prevState,
      rangeTypeToSelectedPeriod: {
        ...rangeTypeToSelectedPeriod,
        [selectedDateRangeType]: newDate,
      },
    }));
  }

  return (
    <SlidingAnimationPageBase
      title={t('EVSE.charging activity')}
      backURL="../device-status"
      includeTopNavHamButton={true}
      includeBottomNav
    >
      {data.savings > 0 && (
        <Center py={3} px={6} color="#C3C2C6">
          <Text textAlign="center" fontSize={22} fontWeight={400} data-testid="charging-activity-title">
            {t('EVSE.charging cost message')} {`${data.savings}%`} {dateRangeSufix}
          </Text>
        </Center>
      )}

      <Box px={3}>
        <DateRangeTypePicker
          dateRangeOptions={DATE_RANGE_OPTIONS}
          selectedDateRangeType={selectedDateRangeType}
          setSelectedDateRangeType={handleDateRangeTypeChange}
        />
        <ActivityDatePicker
          isLoaded={isLoaded}
          selectedDateRangeType={selectedDateRangeType}
          rangeTypeToSelectedPeriod={rangeTypeToSelectedPeriod}
          onChangeDate={handleDateChange}
        />

        <ChargingTotalCostCard totalCost={data.totalCost} dateRangeType={selectedDateRangeType} />

        <SimpleGrid gap={3} columns={2} mt={4} w="100%">
          <DataSummaryCard
            title={t('EVSE.total charge time')}
            isLoaded={!!isLoaded}
            value={data.totalChargeTime}
            suffix={'hrs'}
            w="100%"
            h="100%"
          />
          <DataSummaryCard
            title={t('EVSE.energy consumed')}
            isLoaded={!!isLoaded}
            value={data.totalEnergyConsumed.toString()}
            suffix={'kWh'}
            w="100%"
            h="100%"
          />
        </SimpleGrid>

        <HistoryChart
          isLoaded={isLoaded}
          data={data}
          selectedDateRangeType={selectedDateRangeType}
          rangeTypeToSelectedPeriod={rangeTypeToSelectedPeriod}
          onChartChange={handleSelectChart}
          isCostViewSelected={isCostViewSelected}
        />
        <ChargingSessionList
          isLoaded={isLoaded}
          selectedDateRangeType={selectedDateRangeType}
          chargingSessions={data.chargingSessions}
        />
      </Box>
    </SlidingAnimationPageBase>
  );
}

/**
 * Private function to convert API response to the format used in the app.
 */
function serializeChargingActivity(
  apiData: ChargingActivityRawData,
  selectedDateRangeType: DateRangeType
): ChargingActivityData {
  let sessions: ChargingActivityRecordsRawData[];
  if (selectedDateRangeType === DateRangeType.Day) {
    sessions = apiData.records[0]?.records || [];
  } else {
    sessions = apiData.records;
  }

  const totalCost = apiData.total_cost.toFixed(2);
  const totalChargeTime = formatSecondsToHHMM(apiData.total_charge_duration);
  const totalEnergyConsumed = apiData.total_energy.toLocaleString('en-US');
  const savings = apiData.smart_control_savings;
  const chargingSessions = sessions.map(
    (session: ChargingActivityRecordsRawData, index: number): ChargingActivitySessionData => ({
      id: index + 1,
      start: session.start,
      end: session.end,
      cost: session.total_cost || session.cost || 0,
      usage: session.usage,
      records: session.records!,
    })
  );

  return { totalCost, totalChargeTime, totalEnergyConsumed, savings, chargingSessions };
}

const fetchChargingActivity = async (
  siteDeviceId: string,
  selectedDateRangeType: DateRangeType,
  rangeTypeToSelectedPeriod: Record<DateRangeType, Date>
) => {
  let aggregationType: AggregationType;
  let startDate: string;
  let endDate: string;

  const startTime = 'T00:00:00.000';
  const endTime = 'T23:59:59.999';
  const selectedDate = new Date(rangeTypeToSelectedPeriod[selectedDateRangeType]);
  const formatedSelectedDate = formatDate(rangeTypeToSelectedPeriod[selectedDateRangeType]);

  if (selectedDateRangeType === DateRangeType.Day) {
    startDate = `${formatedSelectedDate}${startTime}`;
    endDate = `${formatedSelectedDate}${endTime}`;
    aggregationType = AggregationType.Session;
  } else if (selectedDateRangeType === DateRangeType.Week) {
    const firstDayOfWeek = getFirstDayOfWeek(selectedDate);
    const mutableFirstDayOfWeek = new Date(firstDayOfWeek);
    const lastDayOfWeek = new Date(mutableFirstDayOfWeek.setDate(mutableFirstDayOfWeek.getDate() + 6));

    startDate = `${format(firstDayOfWeek, 'yyyy-MM-dd')}${startTime}`;
    endDate = `${format(lastDayOfWeek, 'yyyy-MM-dd')}${endTime}`;
    aggregationType = AggregationType.Day;
  } else if (selectedDateRangeType === DateRangeType.Month) {
    const firstDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);

    startDate = `${format(firstDayOfMonth, 'yyyy-MM-dd')}${startTime}`;
    endDate = `${format(lastDayOfMonth, 'yyyy-MM-dd')}${endTime}`;
    aggregationType = AggregationType.Day;
  } else {
    startDate = `${selectedDate.getFullYear()}-01-01${startTime}`;
    endDate = `${selectedDate.getFullYear() + 1}-01-01${startTime}`;
    aggregationType = AggregationType.Month;
  }
  const uri =
    `/v1/ev_chargers/${siteDeviceId}/history?start_date=${startDate}&end_date=${endDate}` +
    `&agg_type=${aggregationType.toLocaleLowerCase()}`;

  const rawData = await get<ChargingActivityRawData>(uri);
  const serialized = serializeChargingActivity(rawData, selectedDateRangeType);

  return serialized;
};
